<script setup lang="ts">
import { Delete, Plus } from "@element-plus/icons-vue";
import { computed, onMounted, ref, watch } from "vue";
import {
  CheckboxesFormElement,
  DefaultAnswer,
  EmailStyleTemplateDto,
  EmailTemplateDto,
  FormElementBase,
  FormElementType,
  LabelledOption,
  RadioButtonsFormElement,
  SelectListFormElement,
  SimpleTextFormElement,
  StatusChangeActions,
  ValidationType,
  WorkflowDefinition,
  WorkflowDefinitionListDto,
  WorkflowEventTriggerType,
  WorkflowStatus,
  WorkflowStatusChangeActionType,
} from "~/api";
import { useMyInfoStore } from "~/stores/MyInfoStore";
import { useOrgInfoStore } from "~/stores/OrgInfoStore";
import { apiClient } from "~/utils/apiClient";
import { emptyGuid } from "~/utils/constants";
import { isValidEmailAddress } from "~/utils/emailUtils";
import {
  workflowEventTriggerTypeDisplayNames,
  workflowStatusChangeActionDisplayNames,
} from "~/utils/enumDisplayNames";
import { showError } from "~/utils/handleAPIError";
const myInfoStore = useMyInfoStore();
const orgInfoStore = useOrgInfoStore();

const props = defineProps<{
  organizationGuid: string;
  emailTemplateDtos: EmailTemplateDto[];
  styleTemplateDtos: EmailStyleTemplateDto[];
  workflowDefinition: WorkflowDefinition;
  eventToEdit?: StatusChangeActions;
  editingIndex?: number;
}>();

const emit = defineEmits<{
  (e: "add:status-change-action", value: StatusChangeActions): void;
  (
    e: "update:status-change-action",
    value: StatusChangeActions,
    index: number,
  ): void;
}>();

const emailTemplates = ref<EmailTemplateDto[]>(props.emailTemplateDtos);
const styleTemplates = ref<EmailStyleTemplateDto[]>(props.styleTemplateDtos);

// Get workflow statuses from the workflow definition
const workflowStatuses = computed(() => {
  return props.workflowDefinition.statusList || [];
});

const form = ref({
  triggerType: WorkflowEventTriggerType.STATUS_CHANGE, // Default to StatusChange using the enum
  actionType: WorkflowStatusChangeActionType.SEND_EMAIL, // Default to SendEmail using the enum
  oldStatus: "", // Empty string means "Any"
  newStatus: "",
  eventCode: "",
  emailTemplateFormResponseGuid: "",
  styleTemplateFormResponseGuid: "",
  sendTo: [] as string[],
  sendFrom: "",
  formEmailInput: "",
  questionGuid: "",
  // Follow-up workflow fields
  targetWorkflowDefinitionGuid: "",
  newWorkflowInitialStatus: "",
  conditionalQuestionGuid: "",
  defaultAnswers: [] as { questionCode: string; answerValue: string }[],
  // Scheduler
  cancelPendingSchedulerItems: false,
});

// Follow-up workflow state
const allWorkflowDefinitions = ref<WorkflowDefinitionListDto[]>([]);
const targetDefinition = ref<WorkflowDefinition | null>(null);
const loadingTargetDefinition = ref(false);

// Load all workflow definitions for the target definition selector
const loadWorkflowDefinitions = async () => {
  try {
    allWorkflowDefinitions.value =
      await apiClient.workflowDefinition.getAllWorkflowDefinitionDtos(
        props.organizationGuid,
      );
  } catch (error) {
    showError("Failed to load workflow definitions:", error);
  }
};

// Statuses for the target workflow definition (for initial status selector)
const targetStatuses = computed<WorkflowStatus[]>(() => {
  if (targetDefinition.value) {
    return targetDefinition.value.statusList || [];
  }
  // Fall back to list DTO statusList if available
  const listDto = allWorkflowDefinitions.value.find(
    (d) => d.guid === form.value.targetWorkflowDefinitionGuid,
  );
  return listDto?.statusList || [];
});

// When the "same definition" option is used (empty guid), show current def's statuses
const effectiveTargetStatuses = computed<WorkflowStatus[]>(() => {
  if (!form.value.targetWorkflowDefinitionGuid) {
    return workflowStatuses.value;
  }
  return targetStatuses.value;
});

// Load full target definition when selection changes (needed for form questions)
watch(
  () => form.value.targetWorkflowDefinitionGuid,
  async (newGuid) => {
    if (newGuid) {
      loadingTargetDefinition.value = true;
      try {
        targetDefinition.value =
          await apiClient.workflowDefinition.getWorkflowDefinition(
            newGuid,
            props.organizationGuid,
          );
      } catch (error) {
        showError("Failed to load target workflow definition:", error);
        targetDefinition.value = null;
      } finally {
        loadingTargetDefinition.value = false;
      }
    } else {
      targetDefinition.value = null;
    }
  },
);

// Flat list of answerable questions from the target definition (for default answers)
const targetQuestions = computed(() => {
  const source = form.value.targetWorkflowDefinitionGuid
    ? targetDefinition.value
    : props.workflowDefinition;
  if (!source?.formDefinitions) return [];
  const questions: {
    questionCode: string;
    label: string;
    formName: string;
    element: FormElementBase;
  }[] = [];
  for (const formDef of source.formDefinitions) {
    for (const comp of formDef.components || []) {
      if (comp.questionCode) {
        questions.push({
          questionCode: comp.questionCode,
          label: comp.label || comp.questionCode,
          formName: formDef.name || formDef.formCode || "",
          element: comp,
        });
      }
    }
  }
  return questions;
});

// Get the options for a specific question (for choice-based elements)
const getOptionsForQuestion = (
  questionCode: string,
): LabelledOption[] | null => {
  const q = targetQuestions.value.find(
    (tq) => tq.questionCode === questionCode,
  );
  if (!q) return null;
  const el = q.element;
  if (el.type === FormElementType.RADIO_BUTTONS) {
    return (
      (el as RadioButtonsFormElement).properties?.radioButtonOptions || null
    );
  }
  if (el.type === FormElementType.SELECT_LIST) {
    return (el as SelectListFormElement).properties?.selectListOptions || null;
  }
  if (el.type === FormElementType.CHECKBOXES) {
    return (el as CheckboxesFormElement).properties?.checkboxOptions || null;
  }
  return null;
};

// Get the form element type for a question by its code
const getElementTypeForQuestion = (
  questionCode: string,
): FormElementType | null => {
  const q = targetQuestions.value.find(
    (tq) => tq.questionCode === questionCode,
  );
  return q?.element?.type ?? null;
};

// Parse a JSON array string into string[] for checkbox multi-select
const getCheckboxValues = (answerValue: string): string[] => {
  try {
    const parsed = JSON.parse(answerValue);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

// Serialize checkbox multi-select values to JSON array string
const setCheckboxValues = (
  da: { questionCode: string; answerValue: string },
  values: string[],
) => {
  da.answerValue = JSON.stringify(values);
};

// Parse a boolean string for the switch component
const getBooleanValue = (answerValue: string): boolean => {
  return answerValue === "true";
};

// Serialize switch value to boolean string
const setBooleanValue = (
  da: { questionCode: string; answerValue: string },
  value: boolean,
) => {
  da.answerValue = value ? "true" : "false";
};

// Checkbox-type questions from the CURRENT workflow (for conditional question)
const nonAnswerableTypes = new Set([
  FormElementType.FORM_TEXT,
  FormElementType.HEADING,
  FormElementType.SECTION,
  FormElementType.ACTION_BUTTON,
  FormElementType.CHANGE_STATUS_BUTTON,
  FormElementType.ANSWER_REVIEW,
]);

const conditionalQuestionOptions = computed(() => {
  const options: { label: string; value: string }[] = [];
  for (const formDef of props.workflowDefinition.formDefinitions || []) {
    for (const comp of formDef.components || []) {
      if (!nonAnswerableTypes.has(comp.type)) {
        options.push({
          label: `${formDef.name || formDef.formCode || ""}: ${comp.label || comp.questionCode}`,
          value: comp.guid,
        });
      }
    }
  }
  return options;
});

const addDefaultAnswer = () => {
  form.value.defaultAnswers.push({ questionCode: "", answerValue: "" });
};

const removeDefaultAnswer = (index: number) => {
  form.value.defaultAnswers.splice(index, 1);
};

const initializeForm = () => {
  if (props.eventToEdit) {
    form.value = {
      triggerType:
        props.eventToEdit.triggerType || WorkflowEventTriggerType.STATUS_CHANGE,
      actionType:
        props.eventToEdit.actionType ||
        WorkflowStatusChangeActionType.SEND_EMAIL,
      oldStatus: props.eventToEdit.oldStatus?.code || "",
      newStatus: props.eventToEdit.newStatus?.code || "",
      eventCode: props.eventToEdit.eventCode || "",
      emailTemplateFormResponseGuid:
        props.eventToEdit.emailTemplateFormResponseGuid || "",
      styleTemplateFormResponseGuid:
        props.eventToEdit.styleTemplateFormResponseGuid || "",
      sendTo: props.eventToEdit.sendTo || [],
      sendFrom: props.eventToEdit.sendFrom || "",
      formEmailInput: "",
      questionGuid: props.eventToEdit.formQuestionGuid || emptyGuid,
      // Follow-up fields
      targetWorkflowDefinitionGuid:
        props.eventToEdit.targetWorkflowDefinitionGuid || "",
      newWorkflowInitialStatus:
        props.eventToEdit.newWorkflowInitialStatus || "",
      conditionalQuestionGuid: props.eventToEdit.conditionalQuestionGuid || "",
      defaultAnswers: (props.eventToEdit.defaultAnswers || []).map((da) => ({
        questionCode: da.questionCode || "",
        answerValue: da.answerValue || "",
      })),
      cancelPendingSchedulerItems:
        props.eventToEdit.cancelPendingSchedulerItems || false,
    };
  }
};

const dialogIsOpen = defineModel<boolean>({ required: true });

const createStatusChangeAction = () => {
  // Build follow-up fields (shared across trigger types)
  const followUpFields = (): Partial<StatusChangeActions> => {
    if (
      form.value.actionType !==
      WorkflowStatusChangeActionType.CREATE_FOLLOW_UP_WORKFLOW
    ) {
      return {};
    }
    const defaultAnswers: DefaultAnswer[] = form.value.defaultAnswers
      .filter((da) => da.questionCode && da.answerValue)
      .map((da) => ({
        questionCode: da.questionCode,
        answerValue: da.answerValue,
      }));
    return {
      targetWorkflowDefinitionGuid:
        form.value.targetWorkflowDefinitionGuid || undefined,
      newWorkflowInitialStatus:
        form.value.newWorkflowInitialStatus || undefined,
      conditionalQuestionGuid: form.value.conditionalQuestionGuid || undefined,
      defaultAnswers: defaultAnswers.length > 0 ? defaultAnswers : undefined,
    };
  };

  if (form.value.triggerType === WorkflowEventTriggerType.STATUS_CHANGE) {
    if (!form.value) {
      showError("Please fill out the form");
      return;
    }

    const newStatus = workflowStatuses.value.find(
      (s) => s.code === form.value.newStatus,
    );
    const oldStatus = form.value.oldStatus
      ? workflowStatuses.value.find((s) => s.code === form.value.oldStatus)
      : null;

    if (!newStatus) {
      showError("Selected target status not found");
      return;
    }

    let processedSendTo: string[] = []; // set to guid instead of "form"
    processedSendTo = form.value.sendTo
      .filter((email) => email.trim() !== "")
      .map((email) => {
        if (email === "Form" && form.value.questionGuid) {
          return form.value.questionGuid;
        }
        return email;
      });

    // Create the status change action
    const statusChangeAction: StatusChangeActions = {
      triggerType:
        (form.value.triggerType as WorkflowEventTriggerType) || undefined,
      actionType:
        (form.value.actionType as WorkflowStatusChangeActionType) || undefined,
      newStatus: newStatus,
      oldStatus: oldStatus || undefined,
      sendTo: processedSendTo,
      sendFrom: form.value.sendFrom,
      emailTemplateFormResponseGuid:
        form.value.emailTemplateFormResponseGuid || undefined,
      styleTemplateFormResponseGuid:
        form.value.styleTemplateFormResponseGuid || undefined,
      formQuestionGuid: form.value.questionGuid || emptyGuid,
      cancelPendingSchedulerItems:
        form.value.cancelPendingSchedulerItems || undefined,
      ...followUpFields(),
    };

    if (props.eventToEdit && props.editingIndex !== undefined) {
      emit(
        "update:status-change-action",
        statusChangeAction,
        props.editingIndex,
      );
    } else {
      emit("add:status-change-action", statusChangeAction);
    }
  }

  if (form.value.triggerType === WorkflowEventTriggerType.ACTION_CODE) {
    if (!form.value) {
      showError("Please fill out the form");
      return;
    }

    let processedSendTo: string[] = []; // set to guid instead of "form"
    processedSendTo = form.value.sendTo
      .filter((email) => email.trim() !== "")
      .map((email) => {
        if (email === "Form" && form.value.questionGuid) {
          return form.value.questionGuid;
        }
        return email;
      });

    // Create the status change action
    const eventCodeAction: StatusChangeActions = {
      triggerType:
        (form.value.triggerType as WorkflowEventTriggerType) || undefined,
      actionType:
        (form.value.actionType as WorkflowStatusChangeActionType) || undefined,
      eventCode: form.value.eventCode || undefined,
      sendTo: processedSendTo,
      sendFrom: form.value.sendFrom,
      emailTemplateFormResponseGuid:
        form.value.emailTemplateFormResponseGuid || undefined,
      styleTemplateFormResponseGuid:
        form.value.styleTemplateFormResponseGuid || undefined,
      formQuestionGuid: form.value.questionGuid || emptyGuid,
      ...followUpFields(),
    };

    if (props.eventToEdit && props.editingIndex !== undefined) {
      emit("update:status-change-action", eventCodeAction, props.editingIndex);
    } else {
      emit("add:status-change-action", eventCodeAction);
    }
  }

  // Close the dialog
  dialogIsOpen.value = false;
};

const emailOptions = [
  {
    label: "ORG",
    value: orgInfoStore.MyOrgInfo?.email || "",
  },
  { label: "LOGGED IN", value: myInfoStore.userInfo.email },
  { label: "SYSTEM", value: "test@manifast.com" },
  { label: "EMAIL FROM FORM ANSWER", value: "Form" },
];

const handleCloseDialog = () => {
  dialogIsOpen.value = false;
};

const allToEmailsAreValid = computed(() => {
  // are all valid?
  for (const code of form.value.sendTo) {
    const trimmedCode = code.trim();
    if (
      trimmedCode !== "Form" && // "Form" is valid since it will be replaced with a guid
      !isValidEmailAddress(trimmedCode) &&
      !trimmedCode.match(
        /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i,
      ) // also valid if it's a guid
    ) {
      return false;
    }
  }
  return true;
});

const isFormQuestionMissing = computed(() => {
  return form.value.sendTo.includes("Form") && !form.value.questionGuid;
});

const hasRequiredFields = computed(() => {
  const isFollowUpAction =
    form.value.actionType ===
    WorkflowStatusChangeActionType.CREATE_FOLLOW_UP_WORKFLOW;
  // Schedule actions (e.g. "cancel pending scheduled items") don't send an email, so they
  // shouldn't require email/style templates — only the trigger identity. Status From stays
  // optional (blank = any status).
  const isScheduleAction =
    form.value.actionType === WorkflowStatusChangeActionType.SCHEDULE_ACTION;

  if (form.value.triggerType === WorkflowEventTriggerType.STATUS_CHANGE) {
    if (isFollowUpAction || isScheduleAction) {
      // Just need the trigger status; a Schedule Action must also enable at least one
      // scheduler behavior (only "cancel pending items" is exposed here) or it's a no-op.
      return (
        Boolean(form.value.newStatus) &&
        (!isScheduleAction || form.value.cancelPendingSchedulerItems)
      );
    }
    return Boolean(
      form.value.newStatus &&
        form.value.emailTemplateFormResponseGuid &&
        form.value.styleTemplateFormResponseGuid &&
        form.value.sendTo.length > 0,
    );
  } else if (form.value.triggerType === WorkflowEventTriggerType.ACTION_CODE) {
    if (isFollowUpAction || isScheduleAction) {
      return (
        Boolean(form.value.eventCode) &&
        (!isScheduleAction || form.value.cancelPendingSchedulerItems)
      );
    }
    return Boolean(
      form.value.eventCode &&
        form.value.emailTemplateFormResponseGuid &&
        form.value.styleTemplateFormResponseGuid &&
        form.value.sendTo.length > 0,
    );
  }
  return false;
});

const formReady = computed(() => {
  const isSendEmail =
    form.value.actionType === WorkflowStatusChangeActionType.SEND_EMAIL;
  return (
    hasRequiredFields.value &&
    (!isSendEmail ||
      (isValidEmailAddress(form.value.sendFrom) && allToEmailsAreValid.value))
  );
});

onMounted(() => {
  emailTemplates.value = props.emailTemplateDtos;
  styleTemplates.value = props.styleTemplateDtos;
  initializeForm();
  loadWorkflowDefinitions();
});
</script>
<template>
  <el-dialog
    v-model="dialogIsOpen"
    :close-on-click-modal="false"
    :close-on-press-escape="false"
    :before-close="handleCloseDialog"
  >
    <template #header>{{
      props.eventToEdit ? "Edit Event" : "Add an Event"
    }}</template>
    <div class="ChangeEventCreator">
      <el-form
        v-model="form"
        class="create-workflow-inputs"
        label-width="auto"
        label-position="left"
      >
        <el-form-item label="Trigger Type">
          <el-select
            v-model="form.triggerType"
            placeholder="Select trigger type"
          >
            <el-option
              v-for="(value, key) in WorkflowEventTriggerType"
              :key="value"
              :label="workflowEventTriggerTypeDisplayNames[value] || key"
              :value="value"
            ></el-option>
          </el-select>
        </el-form-item>
        <el-form-item
          v-if="form.triggerType === WorkflowEventTriggerType.STATUS_CHANGE"
          label="Status To"
        >
          <el-select
            v-model="form.newStatus"
            placeholder="Select status"
            clearable
            filterable
          >
            <el-option
              v-for="status in workflowStatuses"
              :key="status.code ?? ''"
              :label="status.label ?? status.code ?? ''"
              :value="status.code ?? ''"
            ></el-option>
          </el-select>
        </el-form-item>

        <el-form-item
          v-if="form.triggerType === WorkflowEventTriggerType.STATUS_CHANGE"
          label="Status From"
        >
          <el-select
            v-model="form.oldStatus"
            placeholder="Select status"
            clearable
            filterable
          >
            <el-option
              v-for="status in workflowStatuses"
              :key="status.code ?? ''"
              :label="status.label ?? status.code ?? ''"
              :value="status.code ?? ''"
            ></el-option>
          </el-select>
        </el-form-item>

        <el-form-item
          v-if="form.triggerType === WorkflowEventTriggerType.ACTION_CODE"
          label="Event Code"
        >
          <el-input
            v-model="form.eventCode"
            placeholder="Input event code"
            clearable
          >
          </el-input>
        </el-form-item>

        <el-form-item label="Action Type">
          <el-select v-model="form.actionType" placeholder="Select action type">
            <el-option
              v-for="(value, key) in WorkflowStatusChangeActionType"
              :key="value"
              :label="workflowStatusChangeActionDisplayNames[value] || key"
              :value="value"
            ></el-option>
          </el-select>
        </el-form-item>

        <el-form-item
          v-if="form.actionType === WorkflowStatusChangeActionType.SEND_EMAIL"
          class="form-header"
          label="Send Email - Details"
        ></el-form-item>

        <el-form-item
          v-if="form.actionType === WorkflowStatusChangeActionType.SEND_EMAIL"
          label="Content Template"
        >
          <el-select
            v-model="form.emailTemplateFormResponseGuid"
            placeholder="Select template"
            filterable
            autocomplete="off"
          >
            <el-option
              v-for="template in emailTemplates"
              :key="template.guid"
              :label="template.name || 'Unnamed Template'"
              :value="template.formResponseGuid ?? ''"
            ></el-option>
          </el-select>
        </el-form-item>

        <el-form-item
          v-if="form.actionType === WorkflowStatusChangeActionType.SEND_EMAIL"
          label="Style Template"
        >
          <el-select
            v-model="form.styleTemplateFormResponseGuid"
            placeholder="Select template"
            clearable
            filterable
          >
            <el-option
              v-for="template in styleTemplates"
              :key="template.guid"
              :label="template.name || 'Unnamed Template'"
              :value="template.formResponseGuid ?? ''"
            ></el-option>
          </el-select>
        </el-form-item>

        <el-form-item
          v-if="form.actionType === WorkflowStatusChangeActionType.SEND_EMAIL"
          label="To"
          :validate-status="!allToEmailsAreValid ? 'error' : ''"
        >
          <div class="to-emails">
            <el-select
              v-model="form.sendTo"
              multiple
              filterable
              allow-create
              default-first-option
              :reserve-keyword="false"
              placeholder="Select or input user email(s)"
            >
              <el-option
                v-for="item in emailOptions"
                :key="item.value"
                :label="item.label"
                :value="item.value"
              />
            </el-select>

            <el-cascader
              v-if="form.sendTo.includes('Form')"
              v-model="form.questionGuid"
              :options="
                workflowDefinition.formDefinitions
                  ?.map((f) => {
                    const children = f.components
                      .map((q) => {
                        const simpleTextElement = q as SimpleTextFormElement;

                        if (
                          q.type == FormElementType.SIMPLE_TEXT &&
                          simpleTextElement.properties?.validationType ==
                            ValidationType.EMAIL
                        ) {
                          return {
                            label: `${q.label}`,
                            value: q.guid,
                          };
                        }
                        return undefined;
                      })
                      .filter((item) => item !== undefined);
                    if (children.length > 0) {
                      return {
                        label: f.name,
                        children,
                      };
                    }
                    return undefined;
                  })
                  .filter((item) => item !== undefined)
              "
              :emit-path="false"
              placeholder="Select form and question"
              :style="{
                borderColor: isFormQuestionMissing ? '#f56c6c' : undefined,
              }"
            />
          </div>
        </el-form-item>

        <el-form-item
          v-if="form.actionType === WorkflowStatusChangeActionType.SEND_EMAIL"
          label="From"
          :validate-status="
            form.sendFrom && !isValidEmailAddress(form.sendFrom) ? 'error' : ''
          "
        >
          <el-input
            v-model="form.sendFrom"
            placeholder="Input sender email"
            clearable
          >
          </el-input>
        </el-form-item>

        <!-- Scheduler: cancel pending items on this status change -->
        <el-form-item label="Cancel Pending Scheduled Items">
          <el-checkbox v-model="form.cancelPendingSchedulerItems" />
          <span
            style="
              margin-left: 8px;
              color: var(--el-text-color-secondary);
              font-size: 12px;
            "
          >
            When checked, all pending scheduler items (reminders, notifications)
            for this workflow and its form answers will be cancelled when this
            status is reached.
          </span>
        </el-form-item>

        <!-- Follow-Up Workflow Configuration -->
        <el-form-item
          v-if="
            form.actionType ===
            WorkflowStatusChangeActionType.CREATE_FOLLOW_UP_WORKFLOW
          "
          class="form-header"
          label="Follow-Up Workflow - Details"
        ></el-form-item>

        <el-form-item
          v-if="
            form.actionType ===
            WorkflowStatusChangeActionType.CREATE_FOLLOW_UP_WORKFLOW
          "
          label="Target Definition"
        >
          <el-select
            v-model="form.targetWorkflowDefinitionGuid"
            placeholder="Same as current (default)"
            clearable
            filterable
          >
            <el-option
              v-for="def in allWorkflowDefinitions"
              :key="def.guid"
              :label="def.name"
              :value="def.guid"
            ></el-option>
          </el-select>
        </el-form-item>

        <el-form-item
          v-if="
            form.actionType ===
            WorkflowStatusChangeActionType.CREATE_FOLLOW_UP_WORKFLOW
          "
          label="Initial Status"
        >
          <el-select
            v-model="form.newWorkflowInitialStatus"
            placeholder="First status (default)"
            clearable
            filterable
          >
            <el-option
              v-for="status in effectiveTargetStatuses"
              :key="status.code ?? ''"
              :label="status.label ?? status.code ?? ''"
              :value="status.code ?? ''"
            ></el-option>
          </el-select>
        </el-form-item>

        <el-form-item
          v-if="
            form.actionType ===
            WorkflowStatusChangeActionType.CREATE_FOLLOW_UP_WORKFLOW
          "
          label="Conditional Question"
        >
          <el-select
            v-model="form.conditionalQuestionGuid"
            placeholder="Always create (default)"
            clearable
            filterable
          >
            <el-option
              v-for="opt in conditionalQuestionOptions"
              :key="opt.value"
              :label="opt.label"
              :value="opt.value"
            ></el-option>
          </el-select>
        </el-form-item>

        <el-form-item
          v-if="
            form.actionType ===
            WorkflowStatusChangeActionType.CREATE_FOLLOW_UP_WORKFLOW
          "
          label="Default Answers"
        >
          <div class="default-answers">
            <div
              v-for="(da, index) in form.defaultAnswers"
              :key="index"
              class="default-answer-row"
            >
              <el-select
                v-model="da.questionCode"
                placeholder="Select question"
                filterable
                class="default-answer-question"
              >
                <el-option-group
                  v-for="formDef in (form.targetWorkflowDefinitionGuid
                    ? targetDefinition
                    : workflowDefinition
                  )?.formDefinitions || []"
                  :key="formDef.guid"
                  :label="formDef.name || formDef.formCode || 'Form'"
                >
                  <el-option
                    v-for="comp in (formDef.components || []).filter(
                      (c) => c.questionCode,
                    )"
                    :key="comp.guid"
                    :label="`${comp.label || comp.questionCode}`"
                    :value="comp.questionCode"
                  ></el-option>
                </el-option-group>
              </el-select>

              <!-- Checkboxes: multi-select storing JSON array -->
              <el-select
                v-if="
                  getElementTypeForQuestion(da.questionCode) ===
                  FormElementType.CHECKBOXES
                "
                :model-value="getCheckboxValues(da.answerValue)"
                multiple
                placeholder="Select values"
                filterable
                class="default-answer-value"
                @update:model-value="setCheckboxValues(da, $event as string[])"
              >
                <el-option
                  v-for="opt in getOptionsForQuestion(da.questionCode)"
                  :key="opt.value"
                  :label="opt.label || opt.value"
                  :value="opt.value"
                ></el-option>
              </el-select>

              <!-- Boolean: toggle switch storing "true"/"false" -->
              <el-switch
                v-else-if="
                  getElementTypeForQuestion(da.questionCode) ===
                  FormElementType.BOOLEAN
                "
                :model-value="getBooleanValue(da.answerValue)"
                class="default-answer-value"
                @update:model-value="setBooleanValue(da, $event as boolean)"
              />

              <!-- RadioButtons / SelectList: single select -->
              <el-select
                v-else-if="getOptionsForQuestion(da.questionCode)"
                v-model="da.answerValue"
                placeholder="Select value"
                filterable
                class="default-answer-value"
              >
                <el-option
                  v-for="opt in getOptionsForQuestion(da.questionCode)"
                  :key="opt.value"
                  :label="opt.label || opt.value"
                  :value="opt.value"
                ></el-option>
              </el-select>

              <!-- SimpleText / other: free-text input -->
              <el-input
                v-else
                v-model="da.answerValue"
                placeholder="Answer value"
                class="default-answer-value"
              />

              <el-button
                :icon="Delete"
                size="small"
                type="danger"
                plain
                @click="removeDefaultAnswer(index)"
              />
            </div>
            <el-button :icon="Plus" size="small" @click="addDefaultAnswer">
              Add Default Answer
            </el-button>
          </div>
        </el-form-item>

        <el-form-item class="create-button">
          <el-button
            type="primary"
            :disabled="!formReady"
            @click="createStatusChangeAction"
            >{{ props.eventToEdit ? "Update" : "Create" }}</el-button
          >
        </el-form-item>
      </el-form>
    </div>
  </el-dialog>
</template>
<style lang="less">
.ChangeEventCreator {
  .create-button {
    margin-top: 1em;
    .el-form-item__content {
      justify-content: end;
    }
  }
  .form-header {
    font-weight: bold;
  }
  .el-input__inner {
    font-size: 14px;
    font-family: poppins, sans-serif;
  }
  .to-emails {
    display: flex;
    gap: 10px;
  }
  .default-answers {
    display: flex;
    flex-direction: column;
    gap: 8px;
    width: 100%;
  }
  .default-answer-row {
    display: flex;
    gap: 8px;
    align-items: center;
  }
  .default-answer-question {
    flex: 1;
  }
  .default-answer-value {
    flex: 1;
  }
}
</style>
